import type { ReactNode } from 'react'
import { Warehouse, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { useSetProjectServiceTier } from '@/api/hooks'
import { ChoicePicker, type PickerChoice } from '@/components/choice-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePicked } from '@/hooks/use-picked'
import { formatApiErrorMessage } from '@/lib/api-error'
import {
  SERVICE_TIER_FILTER_OPTIONS,
  SERVICE_TIER_LABELS,
  SERVICE_TIERS,
  type ServiceTier,
  type ServiceTierFilterValue,
} from '@/lib/service-tier'
import { cn } from '@/lib/utils'

const TIER_STYLE: Record<ServiceTier, { className: string; icon?: ReactNode }> = {
  // Standard runs itself: the quietest outline, so Service and Planned stand out in a list.
  standard: { className: 'text-muted-foreground ring-border' },
  service: {
    className: 'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/50 dark:text-violet-200 dark:ring-violet-900',
    icon: <Zap className="size-3" aria-hidden />,
  },
  planned: {
    className: 'bg-stone-100 text-stone-700 ring-stone-300 dark:bg-stone-900/60 dark:text-stone-200 dark:ring-stone-700',
    icon: <Warehouse className="size-3" aria-hidden />,
  },
}

/** A project's service tier as a small pill: Service and Planned carry an icon, Standard stays quiet. */
export function ServiceTierTag({ tier, className }: { tier: ServiceTier; className?: string }) {
  const style = TIER_STYLE[tier]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
        style.className,
        className,
      )}
    >
      {style.icon}
      {SERVICE_TIER_LABELS[tier]}
    </span>
  )
}

const TIER_CHOICES: PickerChoice[] = SERVICE_TIERS.map((tier) => ({
  value: tier,
  label: SERVICE_TIER_LABELS[tier],
  render: <ServiceTierTag tier={tier} />,
}))

/** Pick Standard, Service or Planned from a tier pill, the way the priority pill picks. */
export function ServiceTierPicker({
  tier,
  onSelect,
  pending,
  disabled,
  align,
}: {
  tier: ServiceTier
  onSelect: (tier: ServiceTier) => void
  pending?: boolean
  disabled?: boolean
  align?: 'start' | 'center' | 'end'
}) {
  return (
    <ChoicePicker
      title="Service tier"
      label={`Service tier: ${SERVICE_TIER_LABELS[tier]}. Change service tier`}
      value={tier}
      choices={TIER_CHOICES}
      pending={pending}
      disabled={disabled}
      align={align}
      onSelect={(value) => onSelect(value as ServiceTier)}
    >
      <ServiceTierTag tier={tier} />
    </ChoicePicker>
  )
}

/**
 * A project's service tier (USA-455); picking one saves it and moves the project's priority to the
 * tier's default. Safe inside row links: clicks never reach the row.
 */
export function ProjectServiceTierPicker({
  projectUuid,
  tier,
  align,
  hideStandard = false,
}: {
  projectUuid: string
  tier: ServiceTier
  align?: 'start' | 'center' | 'end'
  /** In lists, where Standard is most rows and its pill would sit beside a "Standard" priority. */
  hideStandard?: boolean
}) {
  const update = useSetProjectServiceTier(projectUuid)
  const { shown, pick, clear } = usePicked(tier)
  if (hideStandard && shown === 'standard') return null
  return (
    <ServiceTierPicker
      tier={shown}
      pending={update.isPending}
      align={align}
      onSelect={(next) => {
        pick(next)
        update.mutate(next, {
          onError: (error) => {
            clear()
            toast.error(formatApiErrorMessage('Service tier not changed', error))
          },
        })
      }}
    />
  )
}

const FILTER_LABELS: Record<ServiceTierFilterValue, string> = {
  all: 'Any service tier',
  standard: 'Standard',
  service: 'Service',
  planned: 'Planned',
}

/** Filter a list by its projects' service tier, beside the project-priority filter. */
export function ServiceTierFilter({
  value,
  onValueChange,
}: {
  value: ServiceTierFilterValue
  onValueChange: (value: ServiceTierFilterValue) => void
}) {
  return (
    <Select value={value} onValueChange={(next) => onValueChange(next as ServiceTierFilterValue)}>
      <SelectTrigger aria-label="Service tier filter" className="w-full md:w-[170px]">
        <SelectValue placeholder="Service tier" />
      </SelectTrigger>
      <SelectContent>
        {SERVICE_TIER_FILTER_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {FILTER_LABELS[option]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
